/**
 * The smallest slice of markdown the assistant actually emits, tokenized.
 *
 * WHY THIS EXISTS. The model writes `**Appium server isn't running.**` and the bubble printed the
 * asterisks literally, so every answer with emphasis read as punctuation noise. The two honest
 * options were "teach the model not to use markdown" or "render the markdown it uses". Rendering
 * won: the answers are genuinely structured - numbered blockers, a setting name, a URL - and
 * stripping that structure to avoid two characters would make them harder to read, not easier.
 *
 * LINKS matter more than the rest. Once the assistant started citing fetched web sources, the whole
 * point of a citation is that the customer can go and check it; a URL printed as text is a citation
 * they have to select and copy by hand.
 *
 * WHY NOT A MARKDOWN LIBRARY. Nothing markdown-shaped is installed, and the full grammar is far
 * more surface than this needs. Bold, inline code and links are what the assistant emits; anything
 * else is left exactly as written, so an unmatched `**` stays visible rather than silently eating
 * the rest of the answer.
 *
 * SECURITY. This returns DATA, not HTML. The caller maps tokens to React elements, so model output
 * is never handed to dangerouslySetInnerHTML and cannot inject markup - which matters because this
 * text comes from a model that is itself fed customer-supplied context. Only http(s) is matched, so
 * a `javascript:` or `data:` URL can never become an href.
 *
 * <h2>And why that is not the whole of the security story</h2>
 * There is no XSS here, but a link is still the one element in an answer that is designed to be
 * clicked without thinking, and the text of an answer is partly derived from content this product
 * FETCHES - a web page, a support thread, anything a prompt injection can reach. Three separate
 * ways of writing an address that goes somewhere other than where it says are refused below:
 *
 *   1. A markdown label that is itself a URL naming a different host than the href. The label is
 *      free text, so `[https://instagram.com/help](https://evil.example/login)` renders as a
 *      trustworthy citation of a site the customer has never been to. When the label CLAIMS to be
 *      an address, the address that is actually followed is what gets shown.
 *   2. Bidi controls in a label. U+202E reverses everything after it, so a label can be written to
 *      read as one domain and be stored as another. Stripped - they have no legitimate use in a
 *      one-line citation label.
 *   3. Userinfo before the host: `https://google.com@evil.com/x` fetches evil.com and reads as
 *      google.com. Never linkified; it stays as the text it is.
 *
 * The anchor also carries the href in its title, so hovering shows the destination even where a
 * browser's own status bar is not visible.
 */

/**
 * In precedence order:
 *   1. a markdown link  [label](https://…)   - matched first so its URL is not caught by rule 4
 *   2. bold             **text**
 *   3. inline code      `text`               - before rule 4, so a URL in backticks stays code
 *   4. a bare URL       https://…
 *
 * Bold and code exclude newlines: an unclosed marker at the end of one paragraph would otherwise
 * swallow everything up to the next one, turning a stray asterisk into a formatting bug that spans
 * the whole answer. Confining a token to its own line keeps the damage to that line.
 *
 * The link URL allows BALANCED parentheses - `[Foo](https://en.wikipedia.org/wiki/Foo_(bar))` is
 * the ordinary shape of a Wikipedia citation, and stopping at the first `)` handed the customer a
 * link that 404s. The alternation is between "not a paren" and "a complete (…) group", so the
 * closing `)` of the markdown link itself still terminates the URL.
 */
const TOKEN = new RegExp(
  '\\[([^\\]\\n]+)\\]\\((https?://(?:[^\\s()]|\\([^\\s()]*\\))+)\\)' + // [label](url)
  '|\\*\\*([^*\\n]+?)\\*\\*' +                      // **bold**
  '|`([^`\\n]+?)`' +                                // `code`
  '|(https?://[^\\s<>"\'`]+)',                      // bare url
  'g'
);

/**
 * Trailing punctuation that is sentence grammar, not part of the address.
 *
 * "See https://x.com/a." and "https://x.com/a, https://x.com/b" are both common, and a link whose
 * href ends in `.` or `,` 404s.
 */
const TRAILING_ONE = /[.,;:!?\]}>'"]/;

/**
 * Bidi controls: overrides, embeddings and isolates.
 *
 * Nothing this renderer shows needs one - inherent character directionality already lays out RTL
 * text correctly - and their only effect on a one-line link label is to make it read as something
 * other than what it says.
 */
const BIDI_G = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const HAS_BIDI = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/;

/**
 * A label that is entirely an address - a host, optionally with a scheme, a port and a path.
 *
 * Deliberately strict: it must be the WHOLE label and the last host segment must be alphabetic, so
 * ordinary prose labels ("Follow limits", "Version 2.1 notes") are never mistaken for one. A label
 * this does not match is a description, and a description is free to say whatever it likes.
 */
const LABEL_AS_URL = /^\s*(?:https?:\/\/)?((?:[a-z0-9-]+\.)+[a-z]{2,})(?::\d+)?(?:[/?#]\S*)?\s*$/i;

/** Everything between the scheme and the first `/`, `?` or `#`: userinfo, host and port. */
function authorityOf(url) {
  const m = /^https?:\/\/([^/?#]*)/i.exec(url);
  return m ? m[1] : '';
}

/**
 * Why this address must not become a clickable link, or null when it may.
 *
 * The `@` rule is the whole point: a browser reads everything before it as userinfo and discards
 * it, so `https://google.com@evil.com/x` displays one domain and fetches another. A control
 * character in an address is refused for the same reason - it can only be there to make the text
 * read as somewhere else.
 */
function hrefProblem(url) {
  const auth = authorityOf(url);
  if (!auth) return 'no host';
  if (auth.indexOf('@') >= 0) return 'userinfo before the host';
  if (HAS_BIDI.test(url)) return 'a bidi control in the address';
  return null;
}

/** Comparable host: no port, no trailing dot, no leading `www.`, lower case. */
function normalizeHost(host) {
  return String(host || '')
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/\.$/, '')
    .replace(/^www\./, '');
}

function hostOfUrl(url) {
  return normalizeHost(authorityOf(url));
}

/** The host a label is claiming to be, or null when the label is ordinary prose. */
function hostOfLabel(label) {
  const m = LABEL_AS_URL.exec(label);
  return m ? normalizeHost(m[1]) : null;
}

/**
 * Drop sentence punctuation from the end of a bare URL.
 *
 * A closing paren gets its own rule, because it is part of the address exactly when the address
 * opened it. `https://en.wikipedia.org/wiki/Foo_(bar)` is a real page and trimming its `)` 404s;
 * `(https://x.com/a)` is a URL in parentheses and keeping the `)` 404s just as surely. Counting
 * decides which one this is; nothing else can.
 */
function trimTrailing(url) {
  let end = url.length;
  while (end > 0) {
    const ch = url.charAt(end - 1);
    if (ch === ')') {
      let opens = 0;
      let closes = 0;
      for (let i = 0; i < end; i += 1) {
        const c = url.charAt(i);
        if (c === '(') opens += 1;
        else if (c === ')') closes += 1;
      }
      if (closes <= opens) break;   // balanced: the paren belongs to the address
      end -= 1;
      continue;
    }
    if (TRAILING_ONE.test(ch)) {
      end -= 1;
      continue;
    }
    break;
  }
  return url.slice(0, end);
}

/**
 * Split `text` into tokens.
 *
 * @param {string} text
 * @returns {Array<{type:'text'|'bold'|'code'|'link', value:string, url?:string}>} never null
 */
export function tokenize(text) {
  if (text === null || text === undefined) return [];
  const s = String(text);
  if (s === '') return [];

  const out = [];
  const pushText = (v) => {
    if (!v) return;
    // Merge with the previous text token so trimmed punctuation does not fragment the output.
    const last = out[out.length - 1];
    if (last && last.type === 'text') last.value += v;
    else out.push({ type: 'text', value: v });
  };

  let last = 0;
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(s)) !== null) {
    if (m.index > last) pushText(s.slice(last, m.index));

    if (m[1] !== undefined) {
      // [label](url) - the label is what the reader sees, the url is where it goes.
      const url = m[2];
      if (hrefProblem(url)) {
        // Refused: show the markdown source as the text it is rather than a link that reads as
        // one destination and goes to another.
        pushText(m[0]);
      } else {
        const label = m[1].replace(BIDI_G, '');
        const claimed = hostOfLabel(label);
        // A label that names a DIFFERENT host than the href is a citation of somewhere the
        // customer will not be taken. Show where the click actually goes.
        out.push({
          type: 'link',
          value: claimed && claimed !== hostOfUrl(url) ? url : label,
          url,
        });
      }
    } else if (m[3] !== undefined) {
      out.push({ type: 'bold', value: m[3] });
    } else if (m[4] !== undefined) {
      out.push({ type: 'code', value: m[4] });
    } else {
      const raw = m[5];
      const trimmed = trimTrailing(raw);
      if (trimmed && !hrefProblem(trimmed)) {
        out.push({ type: 'link', value: trimmed, url: trimmed });
        // Whatever was trimmed is ordinary punctuation and belongs back in the sentence.
        pushText(raw.slice(trimmed.length));
      } else {
        pushText(raw);
      }
    }
    last = m.index + m[0].length;
  }
  if (last < s.length) pushText(s.slice(last));
  return out;
}

export default tokenize;
